import { useCallback, useEffect, useMemo, useState } from 'react';
import surveyData from '../../../../../../../mock-data/survey_defaults.json';

const sd = surveyData.data;

function normPhrase(s) {
  return String(s).toLowerCase().replace(/[^\w\s'\-/]/g, ' ').replace(/\s+/g, ' ').trim();
}

const WC_THEMES = [
  { id: 'clarity',   stroke: '#C8A400', bg: 'rgba(255,242,184,0.22)',
    keys: ['clear', 'direct', 'candid', 'honest', 'straight', 'articulate', 'precise', 'incisive', 'transparent'] },
  { id: 'calm',      stroke: '#6A3AAA', bg: 'rgba(224,208,248,0.22)',
    keys: ['calm', 'steady', 'pressure', 'quiet', 'still', 'unshowy', 'practical', 'never flap', 'cool', 'walking', 'keeps', 'consistent'] },
  { id: 'listening', stroke: '#3A7A1A', bg: 'rgba(214,232,198,0.22)',
    keys: ['listen', 'hear', 'remember', 'attentive', 'notice', 'generous', 'makes', 'kind', 'welcoming', 'warmth', 'warm', 'loyal', 'loyalty', 'long-haul'] },
  { id: 'integrity', stroke: '#AA3010', bg: 'rgba(249,214,196,0.22)',
    keys: ['fair', 'integrity', 'trust', 'principled', 'right', 'rigorous', 'thorough', 'rigour', 'standing', 'sceptic', 'equitable', 'ethical'] },
  { id: 'wit',       stroke: '#006090', bg: 'rgba(214,238,248,0.22)',
    keys: ['humour', 'humor', 'funny', 'wit', 'dry', 'curious', 'playful', 'wise', 'wisdom', 'head girl', 'troublemaker'] },
  { id: 'impact',    stroke: '#806000', bg: 'rgba(245,236,214,0.22)',
    keys: ['gravity', 'presence', 'room', 'gravitas', 'hard conversation', 'reads', 'anticipate', 'two moves', 'depth', 'senior'] },
];

function splitLines(text, lim) {
  if (text.length <= lim) return [text];
  const words = text.split(' ');
  if (words.length < 2) return [text];
  let l1 = words[0];
  for (let i = 1; i < words.length - 1; i++) {
    if ((l1 + ' ' + words[i]).length <= lim) l1 += ' ' + words[i];
    else break;
  }
  const l2 = text.slice(l1.length).trim();
  return l2 ? [l1, l2] : [text];
}

/* ---------- Word cloud ---------- */

function WordCloud({ clusters, tags, filter, onTag }) {
  const VW = 900, VH = 530;
  const COLS = 3, ROWS = 2;
  const CELL_W = VW / COLS;
  const CELL_H = VH / ROWS;
  const ZONE_PX = 18, ZONE_PY = 14;
  const GOLDEN = 2.39996;
  const CHAR_W = 0.65;
  const BOX_PAD = 6;

  const { nodes, ellipses } = useMemo(() => {
    if (!clusters.length) return { nodes: [], ellipses: [] };

    const buckets = WC_THEMES.map(() => []);
    const taken = new Set();
    clusters.forEach(c => {
      const lo = c.label.toLowerCase();
      let best = 0, bi = -1;
      WC_THEMES.forEach((t, i) => {
        const s = t.keys.reduce((a, k) => a + (lo.includes(k) ? 1 : 0), 0);
        if (s > best) { best = s; bi = i; }
      });
      if (bi >= 0 && best > 0) { buckets[bi].push(c); taken.add(c.key); }
    });
    clusters.forEach(c => {
      if (taken.has(c.key)) return;
      let min = Infinity, mi = 0;
      buckets.forEach((b, i) => { if (b.length < min) { min = b.length; mi = i; } });
      buckets[mi].push(c);
    });
    buckets.forEach(b => b.sort((a, z) => z.count - a.count));

    const maxCount = Math.max(...clusters.map(c => c.count), 1);

    function bbox(label, fs) {
      const lim = Math.max(10, Math.floor(15 + fs * 0.55));
      const lines = splitLines(label, lim);
      const w = Math.max(...lines.map(l => l.length)) * fs * CHAR_W + 6;
      const h = lines.length * fs * 1.28 + 2;
      return { w, h };
    }

    function collides(placed, tx, ty, w, h) {
      for (const p of placed) {
        if (Math.abs(tx - p.x) < (w + p.w) / 2 + BOX_PAD &&
            Math.abs(ty - p.y) < (h + p.h) / 2 + BOX_PAD) return true;
      }
      return false;
    }

    const nodes = [], ellipses = [];

    buckets.forEach((bucket, bi) => {
      if (!bucket.length) return;
      const col = bi % COLS, row = Math.floor(bi / COLS);
      const cx = col * CELL_W + CELL_W / 2;
      const cy = row * CELL_H + CELL_H / 2;
      const t = WC_THEMES[bi];
      const zx1 = col * CELL_W + ZONE_PX, zx2 = (col + 1) * CELL_W - ZONE_PX;
      const zy1 = row * CELL_H + ZONE_PY, zy2 = (row + 1) * CELL_H - ZONE_PY;

      const GRID_STEP = 10;
      const fallback = [];
      for (let gx = zx1; gx <= zx2; gx += GRID_STEP)
        for (let gy = zy1; gy <= zy2; gy += GRID_STEP)
          fallback.push([gx, gy, (gx - cx) ** 2 + (gy - cy) ** 2]);
      fallback.sort((a, b) => a[2] - b[2]);

      const cellPlaced = [];
      bucket.forEach(c => {
        const fs = Math.max(9, Math.min(16, 8.5 + (c.count / maxCount) * 7.5));
        const { w, h } = bbox(c.label, fs);
        let px, py, placed = false;

        for (let step = 0; step < 600 && !placed; step++) {
          const angle = step * GOLDEN;
          const r = Math.sqrt(step) * 7;
          const rawX = cx + Math.cos(angle) * r;
          const rawY = cy + Math.sin(angle) * r * 0.8;
          if (rawX < zx1 - w * 0.6 || rawX > zx2 + w * 0.6 ||
              rawY < zy1 - h * 0.6 || rawY > zy2 + h * 0.6) continue;
          const tx = Math.max(zx1 + w / 2, Math.min(zx2 - w / 2, rawX));
          const ty = Math.max(zy1 + h / 2, Math.min(zy2 - h / 2, rawY));
          if (!collides(cellPlaced, tx, ty, w, h)) { px = tx; py = ty; placed = true; }
        }

        if (!placed) {
          for (const [gx, gy] of fallback) {
            const tx = Math.max(zx1 + w / 2, Math.min(zx2 - w / 2, gx));
            const ty = Math.max(zy1 + h / 2, Math.min(zy2 - h / 2, gy));
            if (!collides(cellPlaced, tx, ty, w, h)) { px = tx; py = ty; placed = true; break; }
          }
        }

        if (!placed) return;
        cellPlaced.push({ x: px, y: py, w, h });
        nodes.push({ ...c, x: px, y: py, fs });
      });

      if (cellPlaced.length > 0) {
        const minX = Math.min(...cellPlaced.map(p => p.x - p.w / 2));
        const maxX = Math.max(...cellPlaced.map(p => p.x + p.w / 2));
        const minY = Math.min(...cellPlaced.map(p => p.y - p.h / 2));
        const maxY = Math.max(...cellPlaced.map(p => p.y + p.h / 2));
        ellipses.push({
          cx: (minX + maxX) / 2, cy: (minY + maxY) / 2,
          rx: Math.min((maxX - minX) / 2 + 14, CELL_W / 2 - 4),
          ry: Math.min((maxY - minY) / 2 + 10, CELL_H / 2 - 4),
          bg: t.bg, stroke: t.stroke,
        });
      }
    });

    return { nodes, ellipses };
  }, [clusters]);

  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} style={{ width: '100%', height: '100%', display: 'block' }}>
      <line x1={CELL_W}     y1={8} x2={CELL_W}     y2={VH - 8} stroke="var(--hairline)" strokeWidth={0.75} />
      <line x1={CELL_W * 2} y1={8} x2={CELL_W * 2} y2={VH - 8} stroke="var(--hairline)" strokeWidth={0.75} />
      <line x1={8} y1={CELL_H} x2={VW - 8} y2={CELL_H} stroke="var(--hairline)" strokeWidth={0.75} />

      {WC_THEMES.map((t, bi) => {
        const col = bi % COLS, row = Math.floor(bi / COLS);
        return (
          <text key={bi} x={col * CELL_W + 9} y={row * CELL_H + 13}
            fontFamily="var(--mono)" fontSize={8}
            fill={t.stroke} opacity={0.55} letterSpacing="0.12em">
            {t.id.toUpperCase()}
          </text>
        );
      })}

      {ellipses.map((e, i) => (
        <ellipse key={i} cx={e.cx} cy={e.cy} rx={e.rx} ry={e.ry}
          fill={e.bg} stroke={e.stroke} strokeWidth={0.75} strokeDasharray="4 3" />
      ))}

      {nodes.map(p => {
        // tags[key] is 'longlist', 'final', or undefined
        const tagKind = tags[p.key];
        const isFinal = tagKind === 'final';
        const inLongList = tagKind != null;

        // In finalise phase: finals full, longlist dimmed, rest invisible.
        // In longlist filter: only longlist items prominent.
        // In untagged filter: longlist items dimmed.
        const opacity =
          filter === 'finalise' ? (isFinal ? 1 : inLongList ? 0.45 : 0.07) :
          filter === 'longlist' ? (inLongList ? 1 : 0.1) :
          filter === 'untagged' ? (inLongList ? 0.15 : 1) :
          1;

        // In finalise phase, only longlist items are clickable
        const clickable = filter !== 'finalise' || inLongList;

        const lim = Math.max(10, Math.floor(15 + p.fs * 0.55));
        const lines = splitLines(p.label, lim);
        const lh = p.fs * 1.28;

        return (
          <g key={p.key}
            onClick={clickable ? () => onTag(p.key) : undefined}
            style={{ cursor: clickable ? 'pointer' : 'default' }}
            opacity={opacity}>
            <title>{p.instances.map(ins => ins.name).join(', ')} · ×{p.count}</title>
            <text x={p.x} textAnchor="middle"
              fontFamily="var(--serif)" fontSize={p.fs}
              fill={isFinal ? '#5C4000' : 'var(--ink)'}
              fontWeight={isFinal ? '700' : p.count >= 4 ? '600' : p.count >= 2 ? '500' : '400'}
              fontStyle={isFinal ? 'italic' : 'normal'}>
              {lines.map((ln, li) => (
                <tspan key={li} x={p.x}
                  y={p.y + (li - (lines.length - 1) / 2) * lh}
                  dominantBaseline="middle">{ln}</tspan>
              ))}
            </text>
            {isFinal && (
              <text x={p.x + lines[0].length * p.fs * 0.27 + 4} y={p.y}
                fontSize={p.fs * 0.55} fill="#5C4000" dominantBaseline="middle">◆</text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

/* ---------- Phase 1: build long list ---------- */

function CollectPanel({ longListItems, longListSize, busy, onSuggest, onRemove, onClear, onFinalise }) {
  const canFinalise = longListSize >= 4;
  const atMax = longListSize >= 10;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', border: '1px solid var(--ink)', minHeight: 0 }}>
      <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--ink)', background: '#FFFBEF' }}>
        <div className="mono tiny" style={{ color: 'var(--ink-3)', letterSpacing: '0.08em', marginBottom: 5 }}>STEP 01 · BUILD YOUR LONG LIST</div>
        <p style={{ fontFamily: 'var(--serif)', fontSize: 12.5, lineHeight: 1.5, color: 'var(--ink-2)', margin: 0 }}>
          Click phrases that feel like you at your best. Aim for ten — you'll cut later. Each person's wording matters less than the <em>pattern</em> it points to.
        </p>
      </div>

      <div style={{ padding: '6px 8px', borderBottom: '1px dashed var(--hairline)', display: 'flex', gap: 6 }}>
        <button onClick={onSuggest} disabled={busy || atMax} className="mono"
          style={{ flex: 1, padding: '5px 8px', border: '1px solid var(--ink)', background: busy ? '#FFFBEF' : 'var(--paper)', fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', color: atMax ? 'var(--ink-4)' : 'var(--ink)', cursor: (busy || atMax) ? 'not-allowed' : 'pointer' }}>
          {busy ? 'Thinking…' : '✨ Suggest'}
        </button>
        <button onClick={onClear} disabled={longListSize === 0} className="mono"
          style={{ padding: '5px 8px', border: '1px solid var(--hairline)', background: 'var(--paper)', fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', color: longListSize === 0 ? 'var(--ink-4)' : 'var(--ink-2)', cursor: longListSize === 0 ? 'not-allowed' : 'pointer' }}>
          Clear
        </button>
      </div>

      <div style={{ padding: '4px 12px', borderBottom: '1px dashed var(--hairline)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span className="mono tiny" style={{ color: 'var(--ink-4)' }}>LONG LIST</span>
        <span className="mono tiny" style={{ color: atMax ? 'var(--ink)' : longListSize >= 6 ? 'var(--ink-2)' : 'var(--ink-4)', fontWeight: atMax ? '600' : '400' }}>
          {longListSize}/10
        </span>
      </div>

      <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        {longListItems.length === 0 && (
          <div className="mono tiny" style={{ padding: '14px 10px', color: 'var(--ink-4)', letterSpacing: '0.05em', textAlign: 'center' }}>
            EMPTY · CLICK PHRASES IN THE CLOUD
          </div>
        )}
        {longListItems.map(c => (
          <div key={c.key} style={{ padding: '5px 9px', borderTop: '1px dashed var(--hairline)', display: 'flex', gap: 6, alignItems: 'baseline' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, color: 'var(--ink)', lineHeight: 1.3 }}>{c.label}</div>
              <div className="mono tiny" style={{ color: 'var(--ink-4)', marginTop: 1 }}>
                ×{c.count} · {[...new Set(c.instances.map(i => i.group))].join(' · ')}
              </div>
            </div>
            <button onClick={() => onRemove(c.key)} className="mono"
              style={{ fontSize: 11, color: 'var(--ink-4)', padding: '0 4px', cursor: 'pointer' }}>×</button>
          </div>
        ))}
      </div>

      <div style={{ padding: '10px 12px', borderTop: '1px solid var(--ink)' }}>
        {!canFinalise && longListSize < 4 && (
          <div className="mono tiny" style={{ color: 'var(--ink-4)', textAlign: 'center', marginBottom: 6 }}>
            {longListSize === 0 ? 'ADD AT LEAST 4 TO CONTINUE' : `ADD ${4 - longListSize} MORE TO CONTINUE`}
          </div>
        )}
        <button onClick={onFinalise} disabled={!canFinalise}
          style={{ width: '100%', padding: '9px 12px', border: '1px solid var(--ink)', background: canFinalise ? 'var(--ink)' : 'var(--paper-2)', color: canFinalise ? 'var(--paper)' : 'var(--ink-4)', fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', cursor: canFinalise ? 'pointer' : 'not-allowed' }}>
          Finalise selection →
        </button>
      </div>
    </div>
  );
}

/* ---------- Phase 2: finalise to 4–5 ---------- */

function FinalisePanel({ longListItems, finals, onToggle, onBack }) {
  const count = finals.size;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', border: '1px solid var(--ink)', minHeight: 0 }}>
      <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--ink)', background: '#F2EEFF' }}>
        <div className="mono tiny" style={{ color: 'var(--ink-3)', letterSpacing: '0.08em', marginBottom: 5 }}>STEP 02 · FINALISE YOUR TRAITS</div>
        <p style={{ fontFamily: 'var(--serif)', fontSize: 12.5, lineHeight: 1.5, color: 'var(--ink-2)', margin: 0 }}>
          Pick 4–5 to lead with. Favour what's <em>distinctive</em> — not just true — and what's <em>evidenced</em> across different relationships, not what you aspire to.
        </p>
      </div>

      <div style={{ padding: '4px 12px', borderBottom: '1px dashed var(--hairline)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span className="mono tiny" style={{ color: 'var(--ink-4)' }}>FINAL SELECTION</span>
        <span className="mono tiny" style={{ color: count >= 4 ? 'var(--ink)' : 'var(--ink-4)' }}>
          {count} OF 4–5
        </span>
      </div>

      <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        {longListItems.map(c => {
          const isFinal = finals.has(c.key);
          const isDisabled = !isFinal && finals.size >= 5;
          return (
            <div key={c.key} onClick={() => !isDisabled && onToggle(c.key)}
              style={{
                padding: '8px 12px', borderTop: '1px dashed var(--hairline)',
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                background: isFinal ? 'rgba(255,242,184,0.5)' : 'transparent',
                display: 'flex', gap: 8, alignItems: 'flex-start',
              }}>
              <span style={{ fontSize: 12, color: isFinal ? '#8A6800' : 'var(--ink-4)', marginTop: 2, flexShrink: 0, lineHeight: 1 }}>
                {isFinal ? '◆' : '○'}
              </span>
              <div style={{ flex: 1 }}>
                <div style={{
                  fontFamily: 'var(--serif)', fontSize: 13,
                  color: isFinal ? '#5C4000' : isDisabled ? 'var(--ink-4)' : 'var(--ink)',
                  fontWeight: isFinal ? '600' : '400',
                  fontStyle: isFinal ? 'italic' : 'normal',
                  lineHeight: 1.3,
                }}>{c.label}</div>
                <div className="mono tiny" style={{ color: 'var(--ink-4)', marginTop: 2 }}>
                  ×{c.count} · {[...new Set(c.instances.map(i => i.group))].join(' · ')}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ padding: '8px 12px', borderTop: '1px solid var(--hairline)' }}>
        <button onClick={onBack} className="mono"
          style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer' }}>
          ← Edit long list
        </button>
      </div>
    </div>
  );
}

/* ---------- Results panel ---------- */

function ResultsPanel({ contacts, replies, arrived, onClose, onAdopt }) {
  const clusters = useMemo(() => {
    const map = new Map();
    replies.forEach((r) => {
      if (!arrived.has(r.contactId)) return;
      const c = contacts.find((x) => x.id === r.contactId);
      if (!c) return;
      r.text.split(/\n+/).map((s) => s.trim()).filter(Boolean).forEach((phrase) => {
        const key = normPhrase(phrase);
        if (!key) return;
        if (!map.has(key)) map.set(key, { key, label: phrase, instances: [] });
        map.get(key).instances.push({ contactId: c.id, name: c.name, group: c.group, original: phrase });
      });
    });
    return [...map.values()]
      .map((c) => ({ ...c, count: c.instances.length, groups: [...new Set(c.instances.map((i) => i.group))] }))
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  }, [arrived, replies, contacts]);

  const [longList, setLongList] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('pe-survey-longlist') || '[]')); } catch { return new Set(); }
  });
  const [finals, setFinals] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('pe-survey-finals') || '[]')); } catch { return new Set(); }
  });
  const [phase, setPhase] = useState('collect');
  const [filter, setFilter] = useState('all');
  const [busy, setBusy] = useState(false);

  useEffect(() => { localStorage.setItem('pe-survey-longlist', JSON.stringify([...longList])); }, [longList]);
  useEffect(() => { localStorage.setItem('pe-survey-finals', JSON.stringify([...finals])); }, [finals]);

  const toggleLongList = useCallback((key) => {
    setLongList(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
        // removing from longlist also removes from finals
        setFinals(f => { const fn = new Set(f); fn.delete(key); return fn; });
      } else if (next.size < 10) {
        next.add(key);
      }
      return next;
    });
  }, []);

  const toggleFinal = useCallback((key) => {
    setFinals(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else if (next.size < 5) next.add(key);
      return next;
    });
  }, []);

  const suggest = useCallback(() => {
    setBusy(true);
    setLongList(prev => {
      const next = new Set(prev);
      // First pass: multi-mention, short, no numbers
      for (const c of clusters) {
        if (next.size >= 10) break;
        if (!next.has(c.key) && c.count >= 2 && c.label.length < 22 && !/[\d%]/.test(c.label))
          next.add(c.key);
      }
      // Second pass: fill remainder with any short phrase
      for (const c of clusters) {
        if (next.size >= 10) break;
        if (!next.has(c.key) && c.label.length < 22 && !/[\d%]/.test(c.label))
          next.add(c.key);
      }
      return next;
    });
    setTimeout(() => setBusy(false), 600);
  }, [clusters]);

  // Tags for WordCloud: 'longlist' or 'final' — drives opacity and styling
  const wcTags = useMemo(() => {
    const t = {};
    longList.forEach(k => { t[k] = finals.has(k) ? 'final' : 'longlist'; });
    return t;
  }, [longList, finals]);

  const onTag = useCallback((key) => {
    if (phase === 'collect') toggleLongList(key);
    else if (longList.has(key)) toggleFinal(key);
  }, [phase, longList, toggleLongList, toggleFinal]);

  const longListItems = useMemo(
    () => clusters.filter(c => longList.has(c.key)),
    [clusters, longList]
  );

  const filterOpts = [
    { k: 'all',      l: 'All phrases', n: clusters.length },
    { k: 'untagged', l: 'Untagged',    n: clusters.filter(c => !longList.has(c.key)).length },
    { k: 'longlist', l: '★ Long list', n: longList.size },
  ];

  // In finalise phase the word cloud uses a special filter mode
  const wcFilter = phase === 'finalise' ? 'finalise' : filter;

  return (
    <div style={{ display: 'grid', gridTemplateRows: 'auto auto 1fr auto', gap: 12, minHeight: 0, height: '100%' }}>
      <div>
        <div className="mono tiny" style={{ color: 'var(--ink-3)', marginBottom: 6 }}>CLUSTER · TAG · NAME</div>
        <div style={{ fontFamily: 'var(--serif)', fontSize: 20, letterSpacing: '-0.01em', lineHeight: 1.2, maxWidth: 900 }}>
          {phase === 'collect'
            ? <>Each phrase from your replies is clustered by theme — size shows frequency. Click to build your <em>long list</em>, or hit ✨ Suggest.</>
            : <>Your long list is shown below. Click each phrase to include it in your final 4–5 <em>character traits</em>.</>}
        </div>
      </div>

      {/* Filter tabs — phase 1 only; display:none collapses the grid row */}
      <div style={{ display: phase === 'collect' ? 'flex' : 'none', gap: 0, border: '1px solid var(--ink)' }}>
        {filterOpts.map((o, i) => (
          <button key={o.k} onClick={() => setFilter(o.k)}
            style={{ flex: 1, padding: '7px 10px', borderRight: i < filterOpts.length - 1 ? '1px solid var(--hairline)' : 0, background: filter === o.k ? 'var(--ink)' : 'var(--paper)', color: filter === o.k ? 'var(--paper)' : 'var(--ink)', fontFamily: 'var(--mono)', fontSize: 10.5, letterSpacing: '0.06em', textTransform: 'uppercase', display: 'flex', justifyContent: 'space-between', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
            <span>{o.l}</span><span style={{ opacity: 0.7 }}>{o.n}</span>
          </button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 12, minHeight: 0 }}>
        <div style={{ border: '1px solid var(--ink)', display: 'grid', gridTemplateRows: 'auto 1fr', minHeight: 0, overflow: 'hidden' }}>
          <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--ink)', background: 'var(--paper-2)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span className="mono tiny" style={{ color: 'var(--ink-3)', letterSpacing: '0.08em' }}>
              {phase === 'collect'
                ? `RESPONSES · ${clusters.length} PHRASES · CLICK TO ADD`
                : `LONG LIST · ${longList.size} PHRASES · CLICK TO SELECT`}
            </span>
            <span className="mono tiny" style={{ color: 'var(--ink-4)' }}>
              {phase === 'collect'
                ? `${longList.size}/10 LISTED`
                : `${finals.size}/5 SELECTED`}
            </span>
          </div>
          <div style={{ minHeight: 0, overflow: 'hidden' }}>
            <WordCloud clusters={clusters} tags={wcTags} filter={wcFilter} onTag={onTag} />
          </div>
        </div>

        {phase === 'collect' ? (
          <CollectPanel
            longListItems={longListItems}
            longListSize={longList.size}
            busy={busy}
            onSuggest={suggest}
            onRemove={toggleLongList}
            onClear={() => { setLongList(new Set()); setFinals(new Set()); }}
            onFinalise={() => { setFilter('all'); setPhase('finalise'); }}
          />
        ) : (
          <FinalisePanel
            longListItems={longListItems}
            finals={finals}
            onToggle={toggleFinal}
            onBack={() => setPhase('collect')}
          />
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <button onClick={onClose} className="mono"
          style={{ fontSize: 11, color: 'var(--ink-3)', letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer' }}>
          ← Back to card
        </button>
        {phase === 'finalise' && (
          <button
            onClick={() => {
              const labels = [...finals].map(k => clusters.find(c => c.key === k)?.label).filter(Boolean);
              onAdopt(labels);
            }}
            disabled={finals.size < 4}
            style={{ padding: '10px 18px', border: '1px solid var(--ink)', background: finals.size >= 4 ? 'var(--ink)' : 'var(--paper-2)', color: finals.size >= 4 ? 'var(--paper)' : 'var(--ink-4)', fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', cursor: finals.size >= 4 ? 'pointer' : 'not-allowed' }}>
            {finals.size >= 4 ? `Save ${finals.size} traits ✓` : `Select ${4 - finals.size} more to save`}
          </button>
        )}
      </div>
    </div>
  );
}

/* ---------- Full-screen overlay ---------- */

export function ResultsOverlay({ onClose, onAdopt }) {
  const arrived = useMemo(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('pe-survey-arrived') || '[]');
      if (saved.length > 0) return new Set(saved);
    } catch { /* ignore */ }
    return new Set(sd.contacts.map(c => c.id));
  }, []);

  useEffect(() => {
    const k = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onClose]);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9100, background: 'var(--paper)', display: 'grid', gridTemplateRows: 'auto 1fr', overflow: 'hidden' }}>
      <div style={{ padding: '14px 22px', borderBottom: '1px solid var(--ink)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 12 }}>
        <div>
          <div className="mono tiny" style={{ color: 'var(--ink-3)', letterSpacing: '0.08em' }}>02 · BRAND BUILDER · CHARACTER ANALYSIS</div>
          <div style={{ fontFamily: 'var(--serif)', fontSize: 24, letterSpacing: '-0.01em', lineHeight: 1.05, marginTop: 4 }}>Analyse responses</div>
        </div>
        <button onClick={onClose} style={{ width: 30, height: 30, border: '1px solid var(--ink)', background: 'var(--paper)', fontSize: 16, lineHeight: 1, cursor: 'pointer' }}>×</button>
      </div>
      <div style={{ padding: '20px 28px 28px', overflow: 'auto', minHeight: 0 }}>
        <ResultsPanel
          contacts={sd.contacts}
          replies={sd.replies}
          arrived={arrived}
          onClose={onClose}
          onAdopt={(labels) => { onAdopt?.(labels); onClose(); }}
        />
      </div>
    </div>
  );
}
